import { Badge } from "@/components/ui/badge"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { Bath, BetweenHorizontalStart, DoorOpen, Toilet } from "lucide-react"
import { Identifier, useRedirect } from "ra-core"
import { ReactNode, useState } from "react"

const statuses = [
  { id: 1, name: "Project Registered" },
  { id: 2, name: "Design Phase" },
  { id: 3, name: "Permits & Approvals" },
  { id: 4, name: "Site Preparation" },
  { id: 5, name: "Foundation Construction" },
  { id: 6, name: "Structure & Framing" },
  { id: 7, name: "Roofing & Exterior" },
  { id: 8, name: "Electrical & Plumbing Installation" },
  { id: 9, name: "Interior Works & Finishing" },
  { id: 10, name: "Inspection & Compliance" },
  { id: 11, name: "Punch List & Final Fixes" },
  { id: 12, name: "Delivered" },
]

const StoreyCard = ({
  id,
  title,
  status,
  level,
  children,
  rooms,
  wcs,
  bathrooms,
  open = false,
}: {
  children?: ReactNode
  id?: Identifier
  title: string | ReactNode
  status?: number
  level?: number | ReactNode
  surface?: number | ReactNode
  rooms?: number | ReactNode
  wcs?: number | ReactNode
  bathrooms?: number | ReactNode
  open?: boolean
  mode?: string
}) => {
  const redirect = useRedirect()
  const [opened, setOpen] = useState(open)
  return (
    <div className="flex-1 space-y-2 w-full pb-1.5">
      <Collapsible
        key={id}
        open={opened}
        onOpenChange={() => setOpen(!opened)}
        className="bg-secondary rounded-md"
      >
        <CollapsibleTrigger className="flex w-full items-center justify-between p-4 text-left hover:bg-muted/50">
          <div className="flex flex-col gap-2 md:flex-row md:items-center">
            <h1
              className="text-l font-bold cursor-pointer"
              onClick={() => redirect(`/storeys/${id}/edit`)}
            >
              {title}
            </h1>
            {status && (
              <Badge className="rounded-full border-none bg-amber-600/10 text-amber-600 focus-visible:ring-amber-600/20 focus-visible:outline-none dark:bg-amber-400/10 dark:text-amber-400 dark:focus-visible:ring-amber-400/40 [a&]:hover:bg-amber-600/5 dark:[a&]:hover:bg-amber-400/5">
                <span
                  className="size-1.5 rounded-full bg-amber-600 dark:bg-amber-400"
                  aria-hidden="true"
                />
                {statuses[status].name}
              </Badge>
            )}
          </div>

          <div className="text-muted-foreground flex flex-wrap gap-3 text-sm ml-2">
            <div className="flex items-center gap-1">
              <BetweenHorizontalStart className="size-3" />
              {level}
              <sup>floor</sup>
            </div>

            <div className="flex items-center gap-1">
              <DoorOpen className="size-3" />
              {rooms}
            </div>
            <div className="flex items-center gap-1">
              <Bath className="size-3" />
              {bathrooms}
            </div>
            <div className="flex items-center gap-1">
              <Toilet className="size-3" />
              {wcs}
            </div>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className="border-t p-4">
          {children}
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}

export default StoreyCard
